import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { batchTabOrFormRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { BatchEnrichFormRequest } from 'models/forms/BatchEnrichFormRequest';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { batchFormEnrichmentFailed } from 'state/batchForm/batchForm.action';
import { batchFormEnrichmentReceivedThunk } from 'state/batchForm/batchForm.thunks';
import type { EpicWithThunk } from 'state/state.common';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

export interface BatchFormEnrichmentRequestedEpicApi {
  enrichForm(request: BatchEnrichFormRequest): Observable<BatchFormResult>;
}

// TODO: batch screen unit test
export function batchFormEnrichmentRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    { httpClient, selectors: { getFormEnrichedData, getFormPendingEnrichmentData, getBatchFormInfos } },
  ) =>
    action$.pipe(
      ofType('FORM_ENRICHMENT_REQUESTED'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => getWorkspaceType(state, tabId) === 'newBatch'),
      mergeMap(([{ tabId, formId }, state]) => {
        const { negotiationId, version } = getBatchFormInfos(state, formId)!;

        const enrichedData = getFormEnrichedData(state, formId);
        const pendingData = getFormPendingEnrichmentData(state, formId);

        const request: BatchEnrichFormRequest = {
          productType: (pendingData.ProductType || enrichedData.ProductType) as string,
          productSubType: (pendingData.ProductSubtype || enrichedData.ProductSubtype) as string,
          wrapper: (pendingData.Wrapper || enrichedData.Wrapper) as string,
          negotiationId,
          version,
          variationParameters: pendingData,
        };

        const tabOrFormRemoved$ = batchTabOrFormRemoved(action$, tabId, formId, state$);

        return batchWorkspaceApiConnector(httpClient)
          .enrichForm(request)
          .pipe(
            takeUntil(tabOrFormRemoved$),
            map(
              ({
                values,
                fieldsMetadata,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                productView: newProductView,
                mustForceAdvancedMode,
                allowedRequests,
              }) =>
                batchFormEnrichmentReceivedThunk(
                  tabId,
                  formId,
                  values['ProductSubtype'] as string,
                  newProductView,
                  fieldsMetadata,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode ?? false,
                  allowedRequests,
                  errors,
                ),
            ),
            timeoutWith(timeoutInMillis, throwError(new Error('timeout')), appScheduler),
            catchError(_ => {
              return of(batchFormEnrichmentFailed(tabId, formId, pendingData));
            }),
          );
      }),
    );
}
